/**
 * Screen 1c, the learner's half — the end of a course.
 *
 * "That's it done." and then, if the course asks for it, the feedback form.
 * **Skip** is recorded: "asked and declined" and "never asked" are different
 * states, and the report has to be able to tell them apart.
 *
 * The only line about privacy on this page is the one the design has — "Your
 * name is sent with your answers" — and it appears only when that is true. Its
 * opposite is not reassured about, which is the first shape CLAUDE.md says to
 * catch: an anonymous form that promises anonymity is a sentence the Worker
 * cannot fully keep, and docs/SECURITY.md is where that nuance belongs.
 */

import { useState } from 'react';
import type { FeedbackQuestionWire } from '@wire';
import { errorText, learn } from '../api';
import { ErrorNote, Footnote, Loading, PageTitle, Panel } from '../components/Chrome';
import { ds } from '../ds';
import { useRouter } from '../router';
import { HAIR, RADIUS, meta, minutesLabel, monoPlain, plural } from '../theme';
import { useLoad } from '../useLoad';

const SCALE = [1, 2, 3, 4, 5];

export default function Done({ id }: { id: string }) {
  const { Button, Input } = ds();
  const { navigate } = useRouter();
  const { data, error, loading, reload } = useLoad(
    () => learn.feedback(id),
    'Could not load the feedback form.',
    id
  );

  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!data) return loading ? <Loading /> : null;

  const { form, summary } = data;
  const s = summary;
  const summaryLine = meta(
    plural(s.lessonCount, 'lesson'),
    minutesLabel(s.minutes),
    s.score === null ? null : `score ${s.score}%`,
    s.late === null
      ? null
      : s.late
        ? `finished ${plural(Math.max(s.daysLate, 1), 'day')} late`
        : 'finished on time'
  );

  // `summary.completedAt` is the only honest answer to "is this done" — a
  // shared link, browser Back, or the Finish button firing ahead of its own
  // commit can all land here first, and the form's own `enabled`/`submitted`
  // pair says nothing about that. `POST .../feedback` refuses an unfinished
  // course, so this is a dead end back to it rather than a form nobody can
  // submit.
  const finished = summary.completedAt !== null;

  if (!finished) {
    return (
      <>
        <PageTitle title="Not finished yet." />
        <Panel style={{ display: 'grid', gap: 14, justifyItems: 'start' }}>
          <p style={{ margin: 0, fontSize: 14.5, color: 'var(--text-muted)' }}>
            Finish the course to send feedback.
          </p>
          <Button variant="primary" size="md" onClick={() => navigate(`/course/${id}`)}>
            Back to the course
          </Button>
        </Panel>
      </>
    );
  }

  const missing = form.questions.filter((q) => q.required && !(answers[q.id] ?? '').trim());

  const send = async () => {
    setBusy(true);
    setFailed(null);
    const scale = form.questions.find((q) => q.template && q.kind === 'scale');
    const rating = scale && answers[scale.id] ? Number(answers[scale.id]) : null;
    try {
      await learn.sendFeedback(id, {
        rating: Number.isFinite(rating) ? rating : null,
        answers: form.questions
          .filter((q) => (answers[q.id] ?? '').trim())
          .map((q) => ({ questionId: q.id, value: answers[q.id]! }))
      });
      await reload();
    } catch (err) {
      setFailed(errorText(err, 'Your feedback did not send.'));
    }
    setBusy(false);
  };

  const skip = async () => {
    setBusy(true);
    setFailed(null);
    try {
      await learn.feedbackSkip(id);
      navigate('/learning');
    } catch (err) {
      setFailed(errorText(err, 'That did not work.'));
      setBusy(false);
    }
  };

  const closing = !form.enabled || data.submitted;

  return (
    <>
      <PageTitle title="That's it done." meta={summaryLine} />

      <ErrorNote>{failed}</ErrorNote>

      {closing ? (
        <Panel style={{ display: 'grid', gap: 14, justifyItems: 'start' }}>
          {data.submitted && <Footnote>Your feedback was sent.</Footnote>}
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="primary" size="md" onClick={() => navigate('/learning')}>
              Back to my learning
            </Button>
            <Button variant="ghost" size="md" onClick={() => navigate(`/course/${id}`)}>
              See the course
            </Button>
          </div>
        </Panel>
      ) : (
        <Panel style={{ display: 'grid', gap: 22 }}>
          {form.questions.map((q) => (
            <Question
              key={q.id}
              question={q}
              value={answers[q.id] ?? ''}
              onChange={(next) => setAnswers((a) => ({ ...a, [q.id]: next }))}
              Input={Input}
            />
          ))}

          {form.named && <Footnote>Your name is sent with your answers.</Footnote>}

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Button
              variant="primary"
              size="md"
              disabled={busy || missing.length > 0}
              onClick={() => void send()}
            >
              Send feedback
            </Button>
            <Button variant="ghost" size="md" disabled={busy} onClick={() => void skip()}>
              Skip
            </Button>
          </div>
        </Panel>
      )}
    </>
  );
}

function Question({
  question,
  value,
  onChange,
  Input
}: {
  question: FeedbackQuestionWire;
  value: string;
  onChange: (next: string) => void;
  Input: ReturnType<typeof ds>['Input'];
}) {
  const label = (
    <span style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--text-strong)' }}>
      {question.label}
      {!question.required && <span style={{ ...monoPlain, marginLeft: 8 }}>optional</span>}
    </span>
  );

  if (question.kind === 'scale') {
    return (
      <div style={{ display: 'grid', gap: 10 }}>
        {label}
        <div style={{ display: 'flex', gap: 8 }}>
          {SCALE.map((n) => {
            const on = value === String(n);
            return (
              <button
                key={n}
                type="button"
                aria-pressed={on}
                onClick={() => onChange(String(n))}
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: RADIUS.control,
                  border: on ? '1px solid transparent' : HAIR,
                  background: on ? 'var(--primary)' : 'var(--surface-card)',
                  color: on ? 'var(--text-on-primary)' : 'var(--text-strong)',
                  fontSize: 15,
                  fontWeight: 600
                }}
              >
                {n}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (question.kind === 'choice') {
    return (
      <div style={{ display: 'grid', gap: 10 }}>
        {label}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {(question.options ?? []).map((option) => {
            const on = value === option;
            return (
              <button
                key={option}
                type="button"
                aria-pressed={on}
                onClick={() => onChange(option)}
                style={{
                  padding: '8px 14px',
                  borderRadius: 999,
                  border: on ? '1px solid transparent' : HAIR,
                  background: on ? 'var(--primary-soft)' : 'var(--surface-card)',
                  color: on ? 'var(--primary-text)' : 'var(--text-body)',
                  fontSize: 13.5
                }}
              >
                {option}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {label}
      <Input multiline rows={4} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
